import React from "react";
import { SERVER, SELECTED_GROUP_KEY, LEAGUE_NAMES_KEY } from "../constants";
import { useUserState } from "./UserContext";

var GroupStateContext = React.createContext();
var GroupDispatchContext = React.createContext();

function readCachedLeagueNames() {
  try {
    return JSON.parse(localStorage.getItem(LEAGUE_NAMES_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function groupReducer(state, action) {
  switch (action.type) {
    case "GROUPS_LOADED":
      // A logged-in user that belongs to nothing is the "none" screen.
      if (!action.groups || action.groups.length === 0) {
        return { ...state, status: "none", groups: [], selectedGroup: null };
      }
      return {
        ...state,
        status: "ready",
        groups: action.groups,
        selectedGroup: action.selectedGroup,
        scoringModes: action.scoringModes || state.scoringModes,
      };
    case "GROUPS_LOADING":
      return { ...state, status: "loading" };
    case "GROUPS_NONE":
      return { ...state, status: "none", groups: [], selectedGroup: null };
    case "GROUPS_ERROR":
      return { ...state, status: "error" };
    case "SELECT_GROUP":
      return { ...state, selectedGroup: action.name };
    case "LEAGUE_NAMES":
      return { ...state, leagueNames: { ...state.leagueNames, ...action.names } };
    case "SET_SCORING_MODE":
      // Patch the affected group's scoring_mode in place so the UI reflects the
      // change without a full /groups refetch.
      return {
        ...state,
        groups: state.groups.map(g =>
          g.name === action.group ? { ...g, scoring_mode: action.mode } : g,
        ),
      };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Fetch the groups `name` belongs to and dispatch the resulting state. When
// `preferGroup` is one of the user's groups it becomes the active group (used
// after joining a group); otherwise the remembered group is restored, falling
// back to the first group.
function loadGroups(dispatch, name, preferGroup) {
  if (!name) {
    dispatch({ type: "GROUPS_NONE" });
    return Promise.resolve();
  }

  dispatch({ type: "GROUPS_LOADING" });

  var url = new URL(SERVER + "/groups");
  url.search = new URLSearchParams({ name: name }).toString();

  return fetch(url)
    .then(response => {
      // An unknown user (404) is treated the same as belonging to no groups.
      if (response.status === 404) {
        return { groups: [] };
      }
      if (!response.ok) {
        throw new Error("Failed to load groups");
      }
      return response.json();
    })
    .then(json => {
      // A super admin should see every group with admin permissions, not just
      // the ones they belong to. /groups/all returns all groups (each already
      // stamped is_admin: true); fall back to the personal list if it fails so
      // a transient error never locks them out of their own groups.
      if (json && json.is_super_admin) {
        var allUrl = new URL(SERVER + "/groups/all");
        allUrl.search = new URLSearchParams({ name: name }).toString();
        return fetch(allUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error("Failed to load all groups");
            }
            return response.json();
          })
          .catch(() => json);
      }
      return json;
    })
    .then(json => {
      var groups = (json && json.groups) || [];
      var scoringModes = (json && json.scoring_modes) || [];
      if (groups.length === 0) {
        dispatch({ type: "GROUPS_NONE" });
        return;
      }
      // Prefer an explicitly requested group, then the remembered one, then
      // default to the first group in the list.
      var remembered = localStorage.getItem(SELECTED_GROUP_KEY);
      var selectedGroup = groups.some(g => g.name === preferGroup)
        ? preferGroup
        : groups.some(g => g.name === remembered)
        ? remembered
        : groups[0].name;
      localStorage.setItem(SELECTED_GROUP_KEY, selectedGroup);
      dispatch({ type: "GROUPS_LOADED", groups, selectedGroup, scoringModes });
    })
    .catch(error => {
      console.debug(error);
      dispatch({ type: "GROUPS_ERROR" });
    });
}

function GroupProvider({ children }) {
  var { isAuthenticated } = useUserState();

  var [state, dispatch] = React.useReducer(groupReducer, {
    status: "loading",
    groups: [],
    selectedGroup: null,
    scoringModes: [],
    leagueNames: readCachedLeagueNames(),
  });

  // Fetch the groups this user belongs to. Re-runs when auth state flips so a
  // fresh login (which sets `name` only after this provider has mounted)
  // triggers a refetch rather than being stuck on the no-groups screen.
  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    loadGroups(dispatch, localStorage.getItem("name"));
  }, [isAuthenticated]);

  // Resolve display names for the selected group's leagues whenever they
  // change. Names come from ESPN via /league_name, so we fetch (and cache)
  // any slug we don't already have a name for.
  var leagues = leaguesForSelected(state);
  var leaguesKey = leagues.join(",");
  React.useEffect(() => {
    var missing = leagues.filter(slug => !state.leagueNames[slug]);
    if (missing.length === 0) {
      return;
    }

    Promise.all(
      missing.map(slug => {
        var url = new URL(SERVER + "/league_name");
        url.search = new URLSearchParams({ league: slug }).toString();
        return fetch(url)
          .then(response => response.json())
          .then(json => [slug, json && json.name ? json.name : slug])
          .catch(() => [slug, slug]);
      }),
    ).then(entries => {
      var names = {};
      for (const [slug, name] of entries) {
        names[slug] = name;
      }
      var merged = { ...readCachedLeagueNames(), ...names };
      localStorage.setItem(LEAGUE_NAMES_KEY, JSON.stringify(merged));
      dispatch({ type: "LEAGUE_NAMES", names });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaguesKey]);

  var value = {
    ...state,
    leagues,
    isAdmin: isAdminForSelected(state),
    scoringMode: scoringModeForSelected(state),
  };

  return (
    <GroupStateContext.Provider value={value}>
      <GroupDispatchContext.Provider value={dispatch}>
        {children}
      </GroupDispatchContext.Provider>
    </GroupStateContext.Provider>
  );
}

function leaguesForSelected(state) {
  var group = state.groups.find(g => g.name === state.selectedGroup);
  return group ? group.leagues : [];
}

// Whether the current user is an admin of the selected group. The per-group
// `is_admin` flag comes from /groups and is stored on each group object.
function isAdminForSelected(state) {
  var group = state.groups.find(g => g.name === state.selectedGroup);
  return !!(group && group.is_admin);
}

// The scoring mode configured for the selected group. The per-group
// `scoring_mode` slug comes from /groups and is stored on each group object.
function scoringModeForSelected(state) {
  var group = state.groups.find(g => g.name === state.selectedGroup);
  return group ? group.scoring_mode : null;
}

function useGroupState() {
  var context = React.useContext(GroupStateContext);
  if (context === undefined) {
    throw new Error("useGroupState must be used within a GroupProvider");
  }
  return context;
}

function useGroupDispatch() {
  var context = React.useContext(GroupDispatchContext);
  if (context === undefined) {
    throw new Error("useGroupDispatch must be used within a GroupProvider");
  }
  return context;
}

// ###########################################################

function selectGroup(dispatch, name) {
  localStorage.setItem(SELECTED_GROUP_KEY, name);
  dispatch({ type: "SELECT_GROUP", name });
}

// Join a group by name + passcode, then reload the user's groups and make the
// joined group active. Rejects with the server's error message on failure so
// the caller can surface it.
async function joinGroup(dispatch, group, passcode) {
  var name = localStorage.getItem("name");
  var response = await fetch(SERVER + "/groups/join", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, group, passcode }),
  });
  var json = await response.json().catch(() => ({}));
  // The endpoint reports validation failures via an `error` field, sometimes
  // with a 200 status, so we can't rely on the HTTP status alone.
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not join group");
  }
  await loadGroups(dispatch, name, group);
}

// Set a group's scoring mode (admin only), then patch it into local state on
// success. Rejects with the server's error message so the caller can surface it.
async function setScoringMode(dispatch, group, mode) {
  var name = localStorage.getItem("name");
  var response = await fetch(SERVER + "/groups/scoring_mode", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, group, mode }),
  });
  var json = await response.json().catch(() => ({}));
  // The endpoint reports validation/authorization failures via an `error`
  // field, so we can't rely on the HTTP status alone.
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not set scoring mode");
  }
  dispatch({ type: "SET_SCORING_MODE", group, mode });
}

// Fetch the list of usernames that belong to a group. Rejects with the server's
// error message so the caller can surface it.
async function getGroupMembers(group) {
  var url = new URL(SERVER + "/groups/members");
  url.search = new URLSearchParams({ group }).toString();
  var response = await fetch(url);
  var json = await response.json().catch(() => ({}));
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not load members");
  }
  return (json && json.members) || [];
}

// Add a username to a group (admin only). Rejects with the server's error message
// so the caller can surface it.
async function addGroupMember(group, member) {
  var name = localStorage.getItem("name");
  var response = await fetch(SERVER + "/groups/add_member", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, group, member }),
  });
  var json = await response.json().catch(() => ({}));
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not add member");
  }
}

// Remove a username from a group (admin only). Rejects with the server's error
// message so the caller can surface it. Does not touch local group state since
// the removed member is normally not the current user.
async function removeGroupMember(group, member) {
  var name = localStorage.getItem("name");
  var response = await fetch(SERVER + "/groups/remove_member", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, group, member }),
  });
  var json = await response.json().catch(() => ({}));
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not remove member");
  }
}

// Leave a group (remove oneself), then reload the user's groups so the list and
// active selection reflect the change. loadGroups falls back to a remaining
// group, or to the no-groups screen when nothing is left. Rejects with the
// server's error message so the caller can surface it.
async function leaveGroup(dispatch, group) {
  var name = localStorage.getItem("name");
  var response = await fetch(SERVER + "/groups/leave", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, group }),
  });
  var json = await response.json().catch(() => ({}));
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not leave group");
  }
  await loadGroups(dispatch, name);
}

// Set a user's password: hash the plaintext client-side (bcrypt cost 10, matching
// the shared default) and store the opaque hash via the passwords API. Rejects
// with the server's error message so the caller can surface it.
async function setUserPassword(member, plaintext) {
  var bcrypt = require("bcryptjs");
  var password_hash = bcrypt.hashSync(plaintext, 10);
  var response = await fetch(SERVER + "/passwords", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: member, password_hash }),
  });
  var json = await response.json().catch(() => ({}));
  if (!response.ok || (json && json.error)) {
    throw new Error((json && json.error) || "Could not set password");
  }
}

export {
  GroupProvider,
  useGroupState,
  useGroupDispatch,
  selectGroup,
  joinGroup,
  setScoringMode,
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
  leaveGroup,
  setUserPassword,
};
